import type { FlyToOptions, Map } from 'maplibre-gl';
import { LngLat } from 'maplibre-gl';
import type { RawCoordinates, Transform } from '../map-types';
import { fitBoundsToPoint } from './fit-bounds-to-point';
import { isPointInBounds } from './is-point-in-bounds';
import { isPointInPaddedBounds } from './is-point-in-padded-bounds';

type RefitBoundsParams = {
  points: RawCoordinates[];
  center: RawCoordinates;
  map: Map;
  transform: Transform;
  flyToOptions?: FlyToOptions;
};

export const refitBounds = ({
  points,
  center,
  map,
  transform,
  flyToOptions = {}
}: RefitBoundsParams) => {
  const shouldRefit = points.some((point) => {
    const pointInBounds = isPointInBounds(point, transform.getBounds());
    const tooCloseToOuterBounds =
      isPointInPaddedBounds(point, transform, 50) && !pointInBounds;
    const tooCloseToInnerBounds =
      !isPointInPaddedBounds(point, transform, -100) && pointInBounds;

    return tooCloseToOuterBounds || tooCloseToInnerBounds;
  });

  if (!shouldRefit) {
    if (transform.zoom !== map.getZoom()) {
      map.fitBounds(transform.getBounds(), {
        zoom: transform.zoom,
        center,
        ...flyToOptions
      });

      return;
    }

    map.flyTo({
      center: center,
      padding: 50,
      ...flyToOptions
    });

    return;
  }

  let prevZoom = Infinity;

  while (prevZoom > transform.zoom) {
    const pointsInViewport = points.filter((point) =>
      isPointInPaddedBounds(point, transform, 45)
    );

    if (!pointsInViewport.length) {
      break;
    }

    const projectedCenter = transform.project(
      new LngLat(center.lng, center.lat)
    );
    const { maxDistancePoint: point } = pointsInViewport.reduce<{
      maxX: number;
      maxY: number;
      maxDistancePoint: RawCoordinates;
    }>(
      (acc, point) => {
        const { maxX, maxY, maxDistancePoint } = acc;
        const projectedPoint = transform.project(
          new LngLat(point.lng, point.lat)
        );
        const distanceX = Math.abs(projectedCenter.x - projectedPoint.x);
        const distanceY = Math.abs(projectedCenter.y - projectedPoint.y);

        return {
          maxDistancePoint: {
            lng: distanceX > maxX ? point.lng : maxDistancePoint.lng,
            lat: distanceY > maxY ? point.lat : maxDistancePoint.lat
          },
          maxX: distanceX,
          maxY: distanceY
        };
      },
      {
        maxX: -Infinity,
        maxY: -Infinity,
        maxDistancePoint: pointsInViewport[0]
      }
    );

    const { zoom } =
      map.cameraForBounds(fitBoundsToPoint(point, center), {
        padding: 50,
        zoom: transform.zoom,
        center
      }) ?? {};
    prevZoom = transform.zoom;

    if (!zoom) {
      break;
    }

    transform.zoom = zoom;
  }

  map.fitBounds(transform.getBounds(), {
    zoom: transform.zoom,
    center,
    ...flyToOptions
  });
};
